import ringtoneUrl from "@/assets/tone/ringtone.mp3";
import ringingUrl from "@/assets/tone/ringing.mp3";

type ToneKind = "incoming" | "outgoing";

let incomingAudio: HTMLAudioElement | null = null;
let outgoingAudio: HTMLAudioElement | null = null;
let active: ToneKind | null = null;
let gestureBound = false;
let audioCtx: AudioContext | null = null;
let beepTimer: number | null = null;

function ensureAudio(kind: ToneKind): HTMLAudioElement {
  if (kind === "incoming") {
    if (!incomingAudio) {
      incomingAudio = new Audio(ringtoneUrl);
      incomingAudio.loop = true;
      incomingAudio.preload = "auto";
    }
    return incomingAudio;
  }
  if (!outgoingAudio) {
    outgoingAudio = new Audio(ringingUrl);
    outgoingAudio.loop = true;
    outgoingAudio.preload = "auto";
  }
  return outgoingAudio;
}

function stopOne(audio: HTMLAudioElement | null) {
  if (!audio) return;
  audio.pause();
  audio.currentTime = 0;
}

function stopBeepFallback() {
  if (beepTimer != null) {
    window.clearInterval(beepTimer);
    beepTimer = null;
  }
}

/** Web Audio beep if mp3 autoplay is blocked — still needs a prior user gesture. */
function startBeepFallback() {
  if (typeof window === "undefined") return;
  stopBeepFallback();
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    void audioCtx.resume();
    const ping = () => {
      if (!audioCtx) return;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.12;
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.35);
    };
    ping();
    beepTimer = window.setInterval(ping, 1200);
  } catch {
    /* ignore */
  }
}

export function playRingtone(kind: ToneKind = "incoming") {
  if (typeof window === "undefined") return;
  if (active === kind) return;

  stopRingtone();
  const audio = ensureAudio(kind);
  audio.muted = false;
  audio.volume = 1;
  active = kind;
  void audio.play().catch((err) => {
    console.warn("Ringtone blocked (click page once to unlock audio)", err);
    if (kind === "incoming") startBeepFallback();
  });
}

export function stopRingtone() {
  stopOne(incomingAudio);
  stopOne(outgoingAudio);
  stopBeepFallback();
  active = null;
}

/** Call after a user gesture so later autoplay (incoming call) is allowed. */
export function unlockRingtoneAudio() {
  if (typeof window === "undefined") return;
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (Ctx) {
      if (!audioCtx) audioCtx = new Ctx();
      void audioCtx.resume();
    }
  } catch {
    /* ignore */
  }
  for (const kind of ["incoming", "outgoing"] as const) {
    const audio = ensureAudio(kind);
    const wasMuted = audio.muted;
    audio.muted = true;
    void audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = wasMuted;
      })
      .catch(() => {
        audio.muted = wasMuted;
      });
  }
}

/** Bind once so any click/key after login unlocks ringtone for FCM pushes. */
export function ensureRingtoneUnlockOnGesture() {
  if (typeof window === "undefined" || gestureBound) return;
  gestureBound = true;
  const unlock = () => {
    unlockRingtoneAudio();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("keydown", unlock);
}
